import { FormEvent, useEffect, useState } from "react";
import { supabase } from "./lib/supabase";
import {
  MB,
  safeSiteAssetExtension,
  SITE_ASSET_BUCKET,
  SITE_IMAGE_TYPES,
  SiteAssetPicker,
} from "./SiteAssetPicker";
import {
  DEFAULT_VEHICLE_BRANDS,
  normalizeVehicleBrands,
} from "./vehicle-brands";
import "./stand-general-settings.css";
import "./site-asset-upload.css";

type Settings = Record<string, unknown> & {
  stand?: Record<string, unknown> & {
    use_stock_reference?: boolean;
    stock_reference_required?: boolean;
    vehicle_brands?: string[];
    logo_url?: string | null;
    founded_year?: number | null;
    slogan?: string;
    signature?: string;
    short_description?: string;
    story?: string;
    mission?: string;
    values?: string[];
    differentiators?: string[];
    showroom_image_url?: string | null;
    direct_phone?: string;
    social_links?: Record<string, string>;
  };
  digital_contacts?: {
    native_chat_enabled?: boolean;
    timezone?: string;
    weekdays?: number[];
    opens_at?: string;
    closes_at?: string;
    lunch_enabled?: boolean;
    lunch_starts_at?: string;
    lunch_ends_at?: string;
    urgent_outside_hours?: boolean;
  };
  communication?: {
    internal_chat_enabled?: boolean;
    internal_chat_scope?: "organization" | "branch";
    support_enabled?: boolean;
  };
};
const weekDays = [
  { value: 1, label: "Seg" },
  { value: 2, label: "Ter" },
  { value: 3, label: "Qua" },
  { value: 4, label: "Qui" },
  { value: 5, label: "Sex" },
  { value: 6, label: "Sáb" },
  { value: 7, label: "Dom" },
];

export function StandGeneralSettings({
  organizationId,
  canManage,
  area = "general",
}: {
  organizationId: string;
  canManage: boolean;
  area?: "general" | "identity" | "references" | "brands" | "communication" | "site" | "site-presence" | "site-attendance";
}) {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [useReference, setUseReference] = useState(false);
  const [referenceRequired, setReferenceRequired] = useState(false);
  const [vehicleBrands, setVehicleBrands] = useState<string[]>([]);
  const [newVehicleBrand, setNewVehicleBrand] = useState("");
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [pendingLogo, setPendingLogo] = useState<File | undefined>();
  const [profile, setProfile] = useState({
    foundedYear: "",
    slogan: "",
    signature: "",
    shortDescription: "",
    story: "",
    mission: "",
    values: "",
    differentiators: "",
    directPhone: "",
    facebook: "",
    instagram: "",
    youtube: "",
    linkedin: "",
    tiktok: "",
    other: "",
  });
  const [showroomUrl, setShowroomUrl] = useState<string | null>(null);
  const [pendingShowroom, setPendingShowroom] = useState<File | undefined>();
  const [chatEnabled, setChatEnabled] = useState(false),
    [weekdays, setWeekdays] = useState<number[]>([1, 2, 3, 4, 5]),
    [opensAt, setOpensAt] = useState("09:00"),
    [closesAt, setClosesAt] = useState("18:00"),
    [lunchEnabled, setLunchEnabled] = useState(false),
    [lunchStartsAt, setLunchStartsAt] = useState("13:00"),
    [lunchEndsAt, setLunchEndsAt] = useState("14:00"),
    [urgentOutside, setUrgentOutside] = useState(true);
  const [internalChatEnabled, setInternalChatEnabled] = useState(true),
    [internalChatScope, setInternalChatScope] = useState<
      "organization" | "branch"
    >("organization"),
    [supportEnabled, setSupportEnabled] = useState(true);

  const load = async () => {
    const { data, error } = await supabase
      .from("organization_settings")
      .select("settings")
      .eq("organization_id", organizationId)
      .single();
    const loaded = (data?.settings as Settings | undefined) ?? null;
    setSettings(loaded);
    setUseReference(loaded?.stand?.use_stock_reference === true);
    setReferenceRequired(loaded?.stand?.stock_reference_required === true);
    setVehicleBrands(normalizeVehicleBrands(loaded?.stand?.vehicle_brands));
    setLogoUrl(loaded?.stand?.logo_url ?? null);
    setPendingLogo(undefined);
    const stand = loaded?.stand;
    const social = stand?.social_links ?? {};
    setProfile({
      foundedYear: stand?.founded_year ? String(stand.founded_year) : "",
      slogan: String(stand?.slogan ?? ""),
      signature: String(stand?.signature ?? ""),
      shortDescription: String(stand?.short_description ?? ""),
      story: String(stand?.story ?? ""),
      mission: String(stand?.mission ?? ""),
      values: Array.isArray(stand?.values) ? stand.values.join("\n") : "",
      differentiators: Array.isArray(stand?.differentiators)
        ? stand.differentiators.join("\n")
        : "",
      directPhone: String(stand?.direct_phone ?? ""),
      facebook: String(social.facebook ?? ""),
      instagram: String(social.instagram ?? ""),
      youtube: String(social.youtube ?? ""),
      linkedin: String(social.linkedin ?? ""),
      tiktok: String(social.tiktok ?? ""),
      other: String(social.other ?? ""),
    });
    setShowroomUrl(stand?.showroom_image_url ?? null);
    setPendingShowroom(undefined);
    setChatEnabled(loaded?.digital_contacts?.native_chat_enabled === true);
    setInternalChatEnabled(
      loaded?.communication?.internal_chat_enabled !== false,
    );
    setInternalChatScope(
      loaded?.communication?.internal_chat_scope ?? "organization",
    );
    setSupportEnabled(loaded?.communication?.support_enabled !== false);
    setWeekdays(loaded?.digital_contacts?.weekdays ?? [1, 2, 3, 4, 5]);
    setOpensAt(loaded?.digital_contacts?.opens_at ?? "09:00");
    setClosesAt(loaded?.digital_contacts?.closes_at ?? "18:00");
    setLunchEnabled(loaded?.digital_contacts?.lunch_enabled === true);
    setLunchStartsAt(loaded?.digital_contacts?.lunch_starts_at ?? "13:00");
    setLunchEndsAt(loaded?.digital_contacts?.lunch_ends_at ?? "14:00");
    setUrgentOutside(loaded?.digital_contacts?.urgent_outside_hours !== false);
    if (error) setNotice(error.message);
  };

  useEffect(() => {
    void load();
  }, [organizationId]);

  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!settings || !canManage) return;
    if (
      lunchEnabled &&
      (lunchStartsAt >= lunchEndsAt ||
        lunchStartsAt < opensAt ||
        lunchEndsAt > closesAt)
    ) {
      setNotice(
        "O horário de almoço deve ficar dentro do horário de atendimento e a hora de regresso deve ser posterior à hora de saída.",
      );
      return;
    }
    setNotice("A guardar a configuração geral…");
    setSaving(true);
    let nextLogoUrl = logoUrl;
    let uploadedLogoPath: string | null = null;
    let nextShowroomUrl = showroomUrl;
    let uploadedShowroomPath: string | null = null;
    if (pendingLogo) {
      uploadedLogoPath = `${organizationId}/logo/${crypto.randomUUID()}.${safeSiteAssetExtension(pendingLogo)}`;
      const { error: uploadError } = await supabase.storage
        .from(SITE_ASSET_BUCKET)
        .upload(uploadedLogoPath, pendingLogo, {
          cacheControl: "31536000",
          contentType: pendingLogo.type,
          upsert: false,
        });
      if (uploadError) {
        setSaving(false);
        setNotice(uploadError.message);
        return;
      }
      nextLogoUrl = supabase.storage
        .from(SITE_ASSET_BUCKET)
        .getPublicUrl(uploadedLogoPath).data.publicUrl;
    }
    if (pendingShowroom) {
      uploadedShowroomPath = `${organizationId}/showroom/${crypto.randomUUID()}.${safeSiteAssetExtension(pendingShowroom)}`;
      const { error: uploadError } = await supabase.storage
        .from(SITE_ASSET_BUCKET)
        .upload(uploadedShowroomPath, pendingShowroom, {
          cacheControl: "31536000",
          contentType: pendingShowroom.type,
          upsert: false,
        });
      if (uploadError) {
        if (uploadedLogoPath)
          await supabase.storage
            .from(SITE_ASSET_BUCKET)
            .remove([uploadedLogoPath]);
        setSaving(false);
        setNotice(uploadError.message);
        return;
      }
      nextShowroomUrl = supabase.storage
        .from(SITE_ASSET_BUCKET)
        .getPublicUrl(uploadedShowroomPath).data.publicUrl;
    }
    const lines = (value: string) =>
      value
        .split("\n")
        .map((item) => item.trim())
        .filter(Boolean);
    const next: Settings = {
      ...settings,
      stand: {
        ...(settings.stand ?? {}),
        use_stock_reference: useReference,
        stock_reference_required: useReference && referenceRequired,
        vehicle_brands: normalizeVehicleBrands(vehicleBrands),
        logo_url: nextLogoUrl,
        founded_year: profile.foundedYear ? Number(profile.foundedYear) : null,
        slogan: profile.slogan.trim(),
        signature: profile.signature.trim(),
        short_description: profile.shortDescription.trim(),
        story: profile.story.trim(),
        mission: profile.mission.trim(),
        values: lines(profile.values),
        differentiators: lines(profile.differentiators),
        showroom_image_url: nextShowroomUrl,
        direct_phone: profile.directPhone.trim(),
        social_links: {
          facebook: profile.facebook.trim(),
          instagram: profile.instagram.trim(),
          youtube: profile.youtube.trim(),
          linkedin: profile.linkedin.trim(),
          tiktok: profile.tiktok.trim(),
          other: profile.other.trim(),
        },
      },
      digital_contacts: {
        ...(settings.digital_contacts ?? {}),
        native_chat_enabled: chatEnabled,
        timezone: "Europe/Lisbon",
        weekdays,
        opens_at: opensAt,
        closes_at: closesAt,
        lunch_enabled: lunchEnabled,
        lunch_starts_at: lunchStartsAt,
        lunch_ends_at: lunchEndsAt,
        urgent_outside_hours: urgentOutside,
      },
      communication: {
        ...(settings.communication ?? {}),
        internal_chat_enabled: internalChatEnabled,
        internal_chat_scope: internalChatScope,
        support_enabled: supportEnabled,
      },
    };
    const { error } = await supabase.rpc("save_stand_general_settings", {
      p_organization_id: organizationId,
      p_settings: next,
    });
    if (error) {
      const paths = [uploadedLogoPath, uploadedShowroomPath].filter(
        Boolean,
      ) as string[];
      if (paths.length)
        await supabase.storage.from(SITE_ASSET_BUCKET).remove(paths);
    }
    if (!error && logoUrl && logoUrl !== nextLogoUrl) {
      const marker = `/storage/v1/object/public/${SITE_ASSET_BUCKET}/`;
      const at = logoUrl.indexOf(marker);
      if (at >= 0) {
        const oldPath = decodeURIComponent(
          logoUrl.slice(at + marker.length).split("?")[0],
        );
        if (oldPath.startsWith(`${organizationId}/logo/`))
          await supabase.storage.from(SITE_ASSET_BUCKET).remove([oldPath]);
      }
    }
    if (!error && showroomUrl && showroomUrl !== nextShowroomUrl) {
      const marker = `/storage/v1/object/public/${SITE_ASSET_BUCKET}/`;
      const at = showroomUrl.indexOf(marker);
      if (at >= 0) {
        const oldPath = decodeURIComponent(
          showroomUrl.slice(at + marker.length).split("?")[0],
        );
        if (oldPath.startsWith(`${organizationId}/showroom/`))
          await supabase.storage.from(SITE_ASSET_BUCKET).remove([oldPath]);
      }
    }
    setSaving(false);
    setNotice(error?.message ?? "Configuração geral guardada.");
    if (!error) await load();
  };

  if (!settings)
    return (
      <div className="settings-future">
        <strong>A preparar as configurações gerais…</strong>
        {notice && <p>{notice}</p>}
      </div>
    );

  return (
    <form className="media-settings" onSubmit={save}>
      {(area === "general" || area === "identity") && <>
      <section className="stand-logo-settings">
        <header>
          <strong>Identidade visual do stand</strong>
          <p>
            Este logótipo identifica o stand no Stand e é aplicado
            automaticamente ao cabeçalho e ao rodapé de qualquer template do
            site.
          </p>
        </header>
        <SiteAssetPicker
          label="Logótipo do stand"
          description="Carregue a versão principal da marca, preferencialmente com fundo transparente."
          requirements="PNG, JPG, WebP, AVIF ou GIF · máximo 5 MB"
          accept={SITE_IMAGE_TYPES.join(",")}
          file={pendingLogo}
          current={logoUrl}
          disabled={!canManage || saving}
          onFile={(file) => {
            if (!SITE_IMAGE_TYPES.includes(file.type))
              return setNotice(
                "O logótipo deve ser uma imagem PNG, JPG, WebP, AVIF ou GIF.",
              );
            if (file.size > 5 * MB)
              return setNotice("O logótipo excede o limite de 5 MB.");
            setPendingLogo(file);
            setNotice(null);
          }}
          onRemove={() => {
            setPendingLogo(undefined);
            setLogoUrl(null);
          }}
        />
      </section>
      <fieldset className="institutional-settings" disabled={!canManage || saving}>
        <header>
          <strong>Perfil institucional</strong>
          <p>
            Informação única da empresa, utilizada automaticamente pelos seis
            templates. Preencha apenas o que pretende apresentar publicamente.
          </p>
        </header>
        <div className="institutional-grid">
          <label>
            <span>Ano de fundação</span>
            <small className="field-help">
              Opcional — se ficar vazio, os templates não apresentam nem calculam anos de atividade.
            </small>
            <input
              type="number"
              min="1800"
              max={new Date().getFullYear()}
              value={profile.foundedYear}
              onChange={(e) =>
                setProfile({ ...profile, foundedYear: e.target.value })
              }
            />
          </label>
          <label>
            <span>Linha direta</span>
            <small className="field-help">
              Opcional — quando vazio, o site mantém apenas os contactos configurados na sede e nas filiais.
            </small>
            <input
              type="tel"
              value={profile.directPhone}
              onChange={(e) =>
                setProfile({ ...profile, directPhone: e.target.value })
              }
            />
          </label>
          <label>
            <span>Slogan principal</span>
            <small className="field-help">
              Opcional — aparece na identidade e no topo; quando vazio, o template usa o texto configurado na página.
            </small>
            <input
              maxLength={120}
              value={profile.slogan}
              onChange={(e) =>
                setProfile({ ...profile, slogan: e.target.value })
              }
            />
          </label>
          <label>
            <span>Assinatura secundária</span>
            <small className="field-help">
              Opcional — complementa o slogan em layouts compatíveis e desaparece quando estiver vazia.
            </small>
            <input
              maxLength={120}
              value={profile.signature}
              onChange={(e) =>
                setProfile({ ...profile, signature: e.target.value })
              }
            />
          </label>
          <label className="full">
            <span>Apresentação curta</span>
            <small className="field-help">
              Opcional — resumo usado no bloco institucional e em rodapés; o espaço desaparece quando vazio.
            </small>
            <textarea
              maxLength={320}
              value={profile.shortDescription}
              onChange={(e) =>
                setProfile({ ...profile, shortDescription: e.target.value })
              }
            />
          </label>
          <label className="full">
            <span>História</span>
            <small className="field-help">
              Opcional — apresentada no bloco institucional; se ficar vazia, os restantes conteúdos recompõem o bloco.
            </small>
            <textarea
              maxLength={3000}
              value={profile.story}
              onChange={(e) =>
                setProfile({ ...profile, story: e.target.value })
              }
            />
          </label>
          <label>
            <span>Missão</span>
            <small className="field-help">Opcional — apresenta o propósito da empresa; não cria um bloco vazio quando não for preenchida.</small>
            <textarea
              maxLength={800}
              value={profile.mission}
              onChange={(e) =>
                setProfile({ ...profile, mission: e.target.value })
              }
            />
          </label>
          <label>
            <span>Valores</span>
            <small className="field-help">
              Opcional — escreva um valor por linha; a lista é omitida quando ficar vazia.
            </small>
            <textarea
              maxLength={1200}
              value={profile.values}
              onChange={(e) =>
                setProfile({ ...profile, values: e.target.value })
              }
            />
          </label>
          <label className="full">
            <span>Diferenciais</span>
            <small className="field-help">
              Opcional — escreva um diferencial real por linha. A lista desaparece quando vazia; evite afirmações não comprováveis.
            </small>
            <textarea
              maxLength={1200}
              value={profile.differentiators}
              onChange={(e) =>
                setProfile({ ...profile, differentiators: e.target.value })
              }
            />
          </label>
        </div>
        <SiteAssetPicker
          label="Imagem exterior ou showroom"
          description="Opcional — se não carregar uma imagem, o template adapta automaticamente a composição, sem apresentar espaços vazios ou imagens genéricas."
          requirements="PNG, JPG, WebP, AVIF ou GIF · máximo 12 MB"
          accept={SITE_IMAGE_TYPES.join(",")}
          file={pendingShowroom}
          current={showroomUrl}
          disabled={!canManage || saving}
          onFile={(file) => {
            if (!SITE_IMAGE_TYPES.includes(file.type))
              return setNotice(
                "A imagem do showroom deve ser PNG, JPG, WebP, AVIF ou GIF.",
              );
            if (file.size > 12 * MB)
              return setNotice(
                "A imagem do showroom excede o limite de 12 MB.",
              );
            setPendingShowroom(file);
            setNotice(null);
          }}
          onRemove={() => {
            setPendingShowroom(undefined);
            setShowroomUrl(null);
          }}
        />
      </fieldset>
      </>}
      {(area === "site" || area === "site-presence") &&
      <fieldset className="institutional-settings" disabled={!canManage || saving}>
        <header>
          <strong>Presença digital</strong>
          <p>
            Ligações oficiais apresentadas nos cabeçalhos ou rodapés quando
            estiverem preenchidas.
          </p>
        </header>
        <div className="institutional-grid social-grid">
          {(
            [
              ["facebook", "Facebook"],
              ["instagram", "Instagram"],
              ["youtube", "YouTube"],
              ["linkedin", "LinkedIn"],
              ["tiktok", "TikTok"],
              ["other", "Outro canal"],
            ] as const
          ).map(([key, label]) => (
            <label key={key}>
              <span>{label}</span>
              <small className="field-help">
                Opcional — endereço completo começando por https://; a ligação não aparece no site quando ficar vazia.
              </small>
              <input
                type="url"
                placeholder="https://…"
                value={profile[key]}
                onChange={(e) =>
                  setProfile({ ...profile, [key]: e.target.value })
                }
              />
            </label>
          ))}
        </div>
      </fieldset>
      }
      {(area === "general" || area === "references" || area === "brands" || area === "communication") && <>
      {(area === "general" || area === "references") && <>
      <div className="media-settings-intro">
        <strong>Identificação das viaturas</strong>
        <p>
          A matrícula é sempre a identificação obrigatória. Ative a referência
          de stock apenas se o stand utilizar códigos internos próprios.
        </p>
      </div>
      <div className="general-reference-settings">
        <label className="simple-checkbox">
          <input
            name="use_stock_reference"
            type="checkbox"
            checked={useReference}
            disabled={!canManage}
            onChange={(event) => {
              setUseReference(event.target.checked);
              if (!event.target.checked) setReferenceRequired(false);
            }}
          />
          <span>
            <strong>Utilizar referência de stock</strong>
            <small>
              Quando ativa, a referência aparece nos formulários e na gestão
              interna do stock. A matrícula continua obrigatória.
            </small>
          </span>
        </label>
        <label className="simple-checkbox">
          <input
            name="stock_reference_required"
            type="checkbox"
            checked={referenceRequired}
            disabled={!canManage || !useReference}
            onChange={(event) => setReferenceRequired(event.target.checked)}
          />
          <span>
            <strong>Referência de stock obrigatória</strong>
            <small>
              Exige uma referência antes de guardar a viatura. Esta opção só se
              aplica quando o stand utiliza referências.
            </small>
          </span>
        </label>
      </div>
      </>}
      {(area === "general" || area === "communication") && <>
      <div className="media-settings-intro contact-settings-title">
        <strong>Comunicação da equipa e suporte</strong>
        <p>
          Defina se os utilizadores podem conversar entre si e quem pode ser
          encontrado quando o stand possui várias filiais.
        </p>
      </div>
      <div className="general-contact-settings">
        <label className="simple-checkbox">
          <input
            type="checkbox"
            checked={internalChatEnabled}
            disabled={!canManage}
            onChange={(event) => setInternalChatEnabled(event.target.checked)}
          />
          <span>
            <strong>Ativar chat interno</strong>
            <small>
              Permite conversas privadas entre utilizadores autorizados do
              próprio stand.
            </small>
          </span>
        </label>
        {internalChatEnabled && (
          <label>
            <span>Âmbito do chat interno</span>
            <small className="field-help">
              Define quem pode iniciar e participar nas conversas internas do
              stand.
            </small>
            <select
              value={internalChatScope}
              disabled={!canManage}
              onChange={(event) =>
                setInternalChatScope(
                  event.target.value as "organization" | "branch",
                )
              }
            >
              <option value="organization">Todo o stand</option>
              <option value="branch">Apenas colegas com filial em comum</option>
            </select>
          </label>
        )}
        <label className="simple-checkbox">
          <input
            type="checkbox"
            checked={supportEnabled}
            disabled={!canManage}
            onChange={(event) => setSupportEnabled(event.target.checked)}
          />
          <span>
            <strong>Ativar suporte Vorno</strong>
            <small>
              O Master e os utilizadores que abrirem pedidos podem acompanhar o
              mesmo histórico no sistema e na caixa de email de Testes.
            </small>
          </span>
        </label>
      </div>
      {notice && (
        <button
          type="button"
          className="stock-notice"
          role="status"
          aria-live="polite"
          disabled={saving}
          onClick={() => setNotice(null)}
        >
          {notice}
          {!saving && " ×"}
        </button>
      )}
      </>}
      {(area === "general" || area === "brands") && <>
      <div className="vehicle-brand-settings">
        <div className="media-settings-intro">
          <strong>Marcas disponíveis</strong>
          <p>
            Estas são as marcas apresentadas no cadastro de viaturas. Pode
            corrigir um nome, retirar marcas que o stand não utiliza ou
            acrescentar outra.
          </p>
        </div>
        <div className="vehicle-brand-add">
          <label>
            <span>Adicionar marca</span>
            <input
              value={newVehicleBrand}
              disabled={!canManage}
              onChange={(event) => setNewVehicleBrand(event.target.value)}
              placeholder="Ex.: Ligier"
            />
          </label>
          <button
            type="button"
            className="stock-secondary"
            disabled={!canManage || !newVehicleBrand.trim()}
            onClick={() => {
              setVehicleBrands((current) =>
                normalizeVehicleBrands([...current, newVehicleBrand]),
              );
              setNewVehicleBrand("");
            }}
          >
            Adicionar
          </button>
          <button
            type="button"
            className="stock-secondary"
            disabled={!canManage}
            onClick={() => setVehicleBrands([...DEFAULT_VEHICLE_BRANDS])}
          >
            Repor lista inicial
          </button>
        </div>
        <div className="vehicle-brand-list">
          {vehicleBrands.map((brand, index) => (
            <div key={`${brand}-${index}`}>
              <input
                aria-label={`Nome da marca ${brand}`}
                value={brand}
                disabled={!canManage}
                onChange={(event) =>
                  setVehicleBrands((current) =>
                    current.map((item, itemIndex) =>
                      itemIndex === index ? event.target.value : item,
                    ),
                  )
                }
              />
              <button
                type="button"
                className="vehicle-brand-remove"
                aria-label={`Apagar a marca ${brand}`}
                title="Apagar marca"
                disabled={!canManage}
                onClick={() =>
                  setVehicleBrands((current) =>
                    current.filter((_, itemIndex) => itemIndex !== index),
                  )
                }
              >
                ×
              </button>
            </div>
          ))}
        </div>
      </div>
      </>}
      </>}
      {(area === "site" || area === "site-attendance") && <>
      <div className="media-settings-intro contact-settings-title">
        <strong>Atendimento pelo site</strong>
        <p>
          Defina quando o chat da viatura recebe contactos e como o sistema deve
          agir quando ninguém estiver online.
        </p>
      </div>
      <div className="general-contact-settings">
        <label className="simple-checkbox">
          <input
            type="checkbox"
            checked={chatEnabled}
            disabled={!canManage}
            onChange={(event) => setChatEnabled(event.target.checked)}
          />
          <span>
            <strong>Ativar chat no site</strong>
            <small>
              Os identificadores de canal “Chat do site” passam a abrir a
              conversa nativa ligada à viatura.
            </small>
          </span>
        </label>
        <label className="simple-checkbox">
          <input
            type="checkbox"
            checked={urgentOutside}
            disabled={!canManage || !chatEnabled}
            onChange={(event) => setUrgentOutside(event.target.checked)}
          />
          <span>
            <strong>Criar tarefa urgente fora do horário</strong>
            <small>
              O contacto é guardado e o responsável recebe uma tarefa urgente
              quando não existir comercial online.
            </small>
          </span>
        </label>
        <fieldset disabled={!canManage || !chatEnabled}>
          <legend>Horário de atendimento</legend>
          <div className="weekday-options">
            {weekDays.map((day) => (
              <label key={day.value}>
                <input
                  type="checkbox"
                  checked={weekdays.includes(day.value)}
                  onChange={(event) =>
                    setWeekdays((current) =>
                      event.target.checked
                        ? [...current, day.value].sort()
                        : current.filter((value) => value !== day.value),
                    )
                  }
                />
                <span>{day.label}</span>
              </label>
            ))}
          </div>
          <div className="contact-hours">
            <label>
              <span>Abre</span>
              <input
                type="time"
                value={opensAt}
                onChange={(event) => setOpensAt(event.target.value)}
              />
            </label>
            <label>
              <span>Fecha</span>
              <input
                type="time"
                value={closesAt}
                onChange={(event) => setClosesAt(event.target.value)}
              />
            </label>
          </div>
          <label className="simple-checkbox lunch-toggle">
            <input
              type="checkbox"
              checked={lunchEnabled}
              onChange={(event) => setLunchEnabled(event.target.checked)}
            />
            <span>
              <strong>Aplicar horário de almoço</strong>
              <small>
                Durante esta pausa os contactos continuam a ser recebidos, mas a
                tarefa só começa a envelhecer quando o stand regressar.
              </small>
            </span>
          </label>
          {lunchEnabled && (
            <div className="contact-hours lunch-hours">
              <label>
                <span>Saída para almoço</span>
                <input
                  type="time"
                  value={lunchStartsAt}
                  min={opensAt}
                  max={closesAt}
                  onChange={(event) => setLunchStartsAt(event.target.value)}
                />
              </label>
              <label>
                <span>Regresso do almoço</span>
                <input
                  type="time"
                  value={lunchEndsAt}
                  min={opensAt}
                  max={closesAt}
                  onChange={(event) => setLunchEndsAt(event.target.value)}
                />
              </label>
            </div>
          )}
        </fieldset>
      </div>
      </>}
      {canManage && (
        <button className="stock-primary" disabled={saving}>
          {saving ? "A guardar…" : "Guardar"}
        </button>
      )}
    </form>
  );
}
